import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ModalWizard from "./ModalWizard";
import { useAuth } from "../../hooks/useAuth";
import { useNoteType } from "../../hooks/useNoteType";
import { getProductLineFlags } from "../../utils/productLineUtils";

function WizardDemoLauncher() {
  const { auth, setAuth, brand } = useAuth();
  const { t } = useTranslation();
  const { noteTypes = [] } = useNoteType();
  const [showDemo, setShowDemo] = useState(false);
  const authBackupRef = useRef();
  const personaRef = useRef(null);

  const organization = (auth?.session?.currentOrganizationName || "").toLowerCase();
  const isHealcoreBrand = (brand || "").toLowerCase() === "healcore";
  // Substring match so org names like "Beta Org", "Beta", or "BETA Sandbox" all qualify.
  const isBetaOrg = /\bbeta\b/.test(organization);
  // Always allow in local dev so any developer running `npm start` can preview.
  const isLocalDev = process.env.NODE_ENV === "development";
  const canLaunchDemo = isBetaOrg || isHealcoreBrand || isLocalDev;
  // Patient onboarding wizard is a peptides-only flow — patients only exist
  // in the peptides product line. Hide it for DME-only orgs (and for demo
  // personas that aren't peptides-enabled).
  const { hasPeptides } = getProductLineFlags(auth?.orgInfo?.productLines);

  const demoProfile = useMemo(() => {
    const availableNotes = {};
    noteTypes.forEach((note) => {
      availableNotes[note.value] = true;
    });

    return {
      firstName: "Demo",
      lastName: "Provider",
      mobilePhone: "3055551234",
      address: "123 Training Ave",
      state: "FL",
      zip: "33101",
      custom: {
        transcriptLanguage: "en",
        doctorType: "",
        credentials: "",
        availableNotes,
        noteSettings: {
          default: {
            hpiStyleMode: "paragraph",
            hpiLengthMode: "comprehensive",
            mdmStyleMode: "paragraph",
            mdmLengthMode: "comprehensive",
            rosMode: "concise",
            physicalExamLengthMode: "comprehensive",
            planOfCareMode: "complex",
          },
        },
        notUS: false,
        newNote: "",
        importNote: "",
      },
    };
  }, [noteTypes]);

  // Patient persona: blank top-level fields so the dev sees empty inputs to
  // type into (placeholders show "First name", DOB shows MM/DD/YYYY, etc.).
  // custom: {} means nothing in custom is overridden — auth.user.custom is
  // spread through unchanged in the merge below, which preserves
  // doctorConsentAuthorizeVersion (so TermsAndWizardGuard doesn't re-fire).
  const patientDemoProfile = {
    firstName: "",
    lastName: "",
    mobilePhone: "",
    dob: "",
    custom: {},
  };

  const openDemo = (persona) => {
    if (!auth?.user) return;
    authBackupRef.current = JSON.parse(JSON.stringify(auth));
    personaRef.current = persona;
    const profile = persona === "patient" ? patientDemoProfile : demoProfile;
    const mergedUser = {
      ...auth.user,
      ...profile,
      custom: {
        ...(auth.user.custom || {}),
        ...profile.custom,
      },
    };
    // Patient demo: rebuild `custom` as a fresh patient persona instead of
    // spreading the real user's custom blob. Mirrors the pattern in
    // OrdersWizardDemoLauncher.js — a single `doctorConsentAuthorizeVersion:
    // "demo"` keeps TermsAndWizardGuard quiet (it only fires when that field
    // is missing) while leaving doctorConsentAuthorizeSignature{,Date} unset,
    // so the ToS step renders unsigned every launch and prior shipping /
    // billing / onboarding state can't leak in either.
    if (persona === "patient") {
      mergedUser.dob = "";
      mergedUser.custom = {
        doctorConsentAuthorizeVersion: "demo",
      };
    }
    setAuth({
      ...auth,
      user: mergedUser,
    });
    setShowDemo(true);
  };

  const closeDemo = () => {
    setShowDemo(false);
    if (authBackupRef.current) {
      setAuth(authBackupRef.current);
      authBackupRef.current = null;
    }
    personaRef.current = null;
  };

  if (!canLaunchDemo) return null;

  return (
    <>
      <div className="right-sidebar-row" onClick={() => openDemo("provider")}>
        <i className="notranslate material-icons-outlined">auto_fix_high</i>
        {t("wizard.demo.launchOnboardingWizard")}
      </div>
      {hasPeptides && (
        <div className="right-sidebar-row" onClick={() => openDemo("patient")}>
          <i className="notranslate material-icons-outlined">auto_fix_high</i>
          {t("wizard.demo.launchPatientOnboardingWizard")}
        </div>
      )}

      {showDemo && <ModalWizard close={closeDemo} isDemoMode={true} demoIsPatient={personaRef.current === "patient"} />}
    </>
  );
}

export default WizardDemoLauncher;
